import React, { useCallback, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ANY_CANDIDATE_LOCATION } from '@constants';
import { RequestItem, SelectOption, useOptionsApi } from '@innowise-group/core';
import { Input, Select } from '@innowise-group/mui-kit';

import { FormSection } from '../form-section';

const CandidateLocationSection: React.FC = () => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext<RequestItem>();
  const { countryOptions, allCitiesOptions, locationOptions } = useOptionsApi();
  const candidateCountry = watch('candidateCountry');
  const hasCandidateCountry = !!candidateCountry && candidateCountry !== ANY_CANDIDATE_LOCATION;

  const candidateCountryOptions = useMemo<SelectOption[]>(() => {
    const officeCountryIds = new Set((locationOptions || []).map((item) => String(item.parent)));
    const { officeCountries, otherCountries } = (countryOptions || []).reduce<{
      officeCountries: SelectOption[];
      otherCountries: SelectOption[];
    }>(
      (accum, item) => {
        if (officeCountryIds.has(String(item.value))) {
          accum.officeCountries.push(item);
        } else {
          accum.otherCountries.push(item);
        }
        return accum;
      },
      { officeCountries: [], otherCountries: [] },
    );

    return [
      { value: ANY_CANDIDATE_LOCATION, title: t('pages.requests.creationModal.anyLocation') },
      ...officeCountries,
      ...otherCountries,
    ];
  }, [countryOptions, locationOptions, t]);

  const candidateCityOptions = useMemo(() => {
    if (!hasCandidateCountry) return [];
    return (allCitiesOptions || []).filter((item) => String(item.parent) === candidateCountry);
  }, [allCitiesOptions, candidateCountry, hasCandidateCountry]);

  const handleCountryChange = useCallback(
    (onChange: (value: string) => void) => (value: string) => {
      onChange(value);
      setValue('candidateCity', '');
    },
    [setValue],
  );

  return (
    <FormSection title={t('pages.requests.creationModal.candidateLocation')}>
      <Controller
        name="candidateCountry"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Select
            value={value}
            onChange={handleCountryChange(onChange)}
            options={candidateCountryOptions}
            renderInput={(params) => (
              <Input
                {...params}
                label={t('pages.candidates.candidateCreation.country')}
                placeholder={t('pages.candidates.candidateCreation.selectCountry')}
                error={!!errors?.candidateCountry?.message}
                helperText={errors?.candidateCountry?.message}
              />
            )}
          />
        )}
      />
      <Controller
        name="candidateCity"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Select
            value={value}
            onChange={onChange}
            options={candidateCityOptions}
            disabled={!hasCandidateCountry}
            disableSortByAlphabet={false}
            renderInput={(params) => (
              <Input
                {...params}
                label={t('pages.candidates.candidateCreation.city')}
                placeholder={t('pages.candidates.candidateCreation.selectCity')}
                error={!!errors?.candidateCity?.message}
                helperText={errors?.candidateCity?.message}
              />
            )}
          />
        )}
      />
    </FormSection>
  );
};

export default CandidateLocationSection;
